import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../../../../config/i18n";
import type { SupportedLanguage } from "../../../../interfaces";
import { useActiveLang } from "../../../../domains/app/hooks/useActiveLang";
import { useChangeLanguage } from "../../../../domains/app/hooks/useChangeLanguage";
import { ListItem } from "../../../ui/ListItem";
import { SettingsSubHeader } from "../SettingsSubHeader";

// English display name + endonym per language, shown verbatim (language names
// are not localized — the picker lists each language by its English name and
// its own endonym, matching the Figma). Owned here because this view is the
// semantic home for the language list; RootView reuses LANGUAGE_LABELS to
// render the current-language subtitle.
export const LANGUAGE_LABELS = {
  en: { name: "English", native: "English" },
  fr: { name: "French (France)", native: "Français" },
  es: { name: "Spanish", native: "Español" },
  de: { name: "German", native: "Deutsch" },
  it: { name: "Italian", native: "Italiano" },
  pt: { name: "Portuguese", native: "Português" },
  nl: { name: "Dutch", native: "Nederlands" },
  ar: { name: "Arabic", native: "العربية" },
} as const satisfies Record<
  SupportedLanguage,
  { name: string; native: string }
>;

// Display order (matches the Figma). Filtered to the actually-supported set.
// The desktop settings read it too: a reader who switches device must not have
// to re-learn the list.
export const LANGUAGE_ORDER = (
  ["fr", "en", "es", "de", "it", "pt", "nl", "ar"] as const
).filter((lang) => SUPPORTED_LANGUAGES.includes(lang));

export function LanguageView(): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const changeLanguage = useChangeLanguage();

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("Language")} />
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pb-12">
        <div className="flex flex-col gap-2">
          {LANGUAGE_ORDER.map((lang) => {
            const label = LANGUAGE_LABELS[lang];
            return (
              <ListItem
                key={lang}
                selected={lang === activeLang}
                affordance={{ kind: "action" }}
                disabled={false}
                onClick={() => {
                  void changeLanguage(lang);
                }}
              >
                <span className="text-ui-body font-semibold text-ds-black">
                  {label.name}
                </span>
                <span className="text-ui-caption text-ds-slate-500">
                  {label.native}
                </span>
              </ListItem>
            );
          })}
        </div>
        <p className="mt-6 text-ui-note text-ds-slate-500">
          {t(
            "The app language will be changed. Scientific content remains in its original language."
          )}
        </p>
      </div>
    </div>
  );
}
